import { useEffect, useRef, useState } from 'react'
import { resolvePageRef } from '@w/content-blocks'
import { ulid } from '@w/utils'
import { withoutActWarning } from '@w/utils-fe'
import { Button } from '@w/ui/components/button'
import { Trash2Icon } from '@w/ui/components/icon'
import { IconButton } from '@w/ui/components/icon-button'
import { Input } from '@w/ui/components/input'
import { locate, plain, pointAt, rows } from '../../core/tree-read'
import { saveSelection, type CoreSelection, type SavedSelection } from '../../core/state'
import { applyLink, closeLink, openLink } from '../../core/link-editing'
import { selectedLinkId, selectedLinkIds } from '../../core/rich-content'
import type { PageSearchResult } from '../../types/page-search.types'
import type { Overlay } from '../view-api'
import { OverlayPortal, panelClass, segment, select, useDismiss, useLiveApi, usePosition, type OverlayApi } from './shared'
import { EDITABLE } from '../dom-selection'

export const LinkMention: Overlay = ({ api }: { api: OverlayApi }) => {
  const [link, setLink] = useState<{ url: string; target: SavedSelection; links: string[]; preview?: string; rect?: DOMRect } | null>(null)
  const [search, setSearch] = useState<{ key: string | null; results: PageSearchResult[]; failed: boolean }>({ key: null, results: [], failed: false })
  const [dismissed, setDismissed] = useState<string | null>(null)
  const [index, setIndex] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const [chooser, setChooser] = useState<HTMLDivElement | null>(null)
  const [dialog, setDialog] = useState<HTMLFormElement | null>(null)
  const [inside, setInside] = useState(false)
  useEffect(() => {
    const root = api.root
    if (!root) return () => {}
    const refresh = () => {
      const active = root.ownerDocument.activeElement
      withoutActWarning(() => { setInside(root.contains(active) || !!chooser?.contains(active) || !!dialog?.contains(active)) })
    }
    refresh()
    root.ownerDocument.addEventListener('focusin', refresh)
    return () => { root.ownerDocument.removeEventListener('focusin', refresh) }
  }, [api.root, chooser, dialog])
  const live = useLiveApi(api)
  const selection = api.state.selection
  const entries = rows(api.state.blocks)
  const at = selection ? locate(entries, selection.focus) : null
  const row = at ? entries[at.row] : null
  const collapsed = selection && selection.anchor.blockId === selection.focus.blockId && selection.anchor.offset === selection.focus.offset && !selection.nodes?.length && !selection.table
  const match = collapsed && row && at && api.mode === 'edit' ? /(?:^|\s)(@|\[\[)([^\]@\n[]*)$/u.exec(plain(row.block).slice(0, at.offset)) : null
  const query = match?.[2] ?? null
  const position = usePosition(api, undefined, false, dialog ?? chooser, { center: !!link, gap: link ? 8 : 3, triggerLength: link ? 0 : (query?.length ?? 0) + (match?.[1].length ?? 0), anchorRect: link?.rect })
  const key = match && row ? `${row.block.id}:${match.index}:${match[1]}:${query}` : null
  const open = inside && !!match && key !== dismissed && !!api.searchPages
  const results = search.key === key ? search.results : []
  const searchState = search.key === key ? search.failed ? 'error' : 'ready' : 'loading'
  useDismiss(open, () => { setDismissed(key) }, { current: chooser })
  useEffect(() => {
    const searchPages = live.current.searchPages
    if (!open || query === null || !searchPages) return () => {}
    let active = true
    const update = (next: PageSearchResult[]) => { if (active) { setIndex(0); setSearch({ key, results: next, failed: false }) } }
    // Search failure is a visible, recoverable chooser state. The callback owner reports transport failures.
    Promise.resolve().then(() => searchPages(query, update)).then(update, () => { if (active) setSearch({ key, results: [], failed: true }) })
    return () => { active = false }
  }, [open, query, key, live])
  const linkOpen = link !== null
  useEffect(() => { if (linkOpen) input.current?.focus() }, [linkOpen])
  useEffect(() => {
    const root = api.root
    if (!root) return () => {}
    const show = (event?: Event, preview = false) => {
      const current = live.current
      if (current.mode !== 'edit') return
      const dom = root.ownerDocument.getSelection()
      const node = dom?.anchorNode
      const anchor = (node instanceof Element ? node : node?.parentElement)?.closest('a')
      const selection = event instanceof CustomEvent && event.detail ? event.detail as CoreSelection : current.state.selection
      const opened = openLink({ ...current.state, selection }, preview && !anchor)
      if (!opened) return
      const group = opened.intent ? `view:link:${ulid()}` : undefined
      const linked = group && opened.intent ? current.dispatchWithHistory(opened.intent, group).state : { ...current.state, selection }
      if (!linked.selection) return
      // The URL input takes native selection focus; keep the document anchor for the dialog.
      const rect = dom?.rangeCount ? dom.getRangeAt(0).getBoundingClientRect() : undefined
      setLink({ url: opened.edit.url || (anchor?.getAttribute('href') ?? ''), target: saveSelection({ ...linked, selection: linked.selection }), links: group ? selectedLinkIds(linked) : [selectedLinkId(linked)].filter(id => id !== null), preview: group, rect })
    }
    const keydown = (event: KeyboardEvent) => {
      if (!(event.target instanceof Element) || !root.contains(event.target)) return
      // Only authored text owns Mod+K: never a learner input or a read-only (preview) field.
      if (event.target.closest('input, textarea, select') ||
        !event.target.closest('[contenteditable]')?.matches(EDITABLE)) return
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault(); event.stopImmediatePropagation()
        // Mod+K always opens the link editor; on a link it offers Remove link (superseded 2026-10-30 by Notion browser check:
        // Cmd+K on a linked word opened `combobox "Edit link or search pages" … option "Remove link"`, the link kept).
        show(undefined, true)
      }
    }
    const click = (event: MouseEvent) => {
      const anchor = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null
      if (!anchor || !root.contains(anchor) || anchor.dataset.pageId) return
      if (live.current.mode === 'edit' && !event.metaKey && !event.ctrlKey) return
      event.preventDefault()
      const url = anchor.href
      if (/^(https?:|mailto:|tel:)/iu.test(url)) window.open(url, '_blank', 'noopener,noreferrer')
    }
    root.addEventListener('core-view-open-link', show)
    document.addEventListener('keydown', keydown, true)
    root.addEventListener('click', click, true)
    return () => { root.removeEventListener('core-view-open-link', show); document.removeEventListener('keydown', keydown, true); root.removeEventListener('click', click, true) }
  }, [api.root, live])
  function choose(result: PageSearchResult) {
    if (!match || !row || !at) return
    const id = typeof result === 'string' ? result : result.id
    const ref = resolvePageRef({ pageId: id, spaceId: typeof result === 'string' ? undefined : result.spaceId }, api.spaceId ?? '')
    const start = at.offset - match[1].length - match[2].length
    select(api, { anchor: pointAt(row, start), focus: pointAt(row, at.offset) })
    api.dispatch({ type: 'insertFromPaste', md: '', fragment: [{ id: '', type: 'p', children: [{ id: '', type: 'pageLink', pageId: id,
      ...(ref.spaceId && ref.spaceId !== api.spaceId ? { spaceId: ref.spaceId } : {}) }] }] })
    setDismissed(key); api.root?.focus()
  }
  useEffect(() => {
    if (!open || !api.root) return () => {}
    const root = api.root
    const keydown = (event: KeyboardEvent) => {
      if (!(event.target instanceof Node) || !root.contains(event.target)) return
      if (!['ArrowUp', 'ArrowDown', 'Enter', 'Tab', 'Escape'].includes(event.key) || event.isComposing) return
      event.preventDefault(); event.stopImmediatePropagation()
      if (event.key === 'Escape') setDismissed(key)
      else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') setIndex(previous => (previous + (event.key === 'ArrowUp' ? -1 : 1) + results.length) % Math.max(1, results.length))
      else if (results[index]) choose(results[index])
    }
    document.addEventListener('keydown', keydown, true)
    return () => { document.removeEventListener('keydown', keydown, true) }
  })
  function removeLink() {
    if (!link) return
    if (api.restore(link.target)) api.dispatch({ type: 'removeLink' })
    setLink(null); api.root?.focus()
  }
  function apply() {
    if (!link) return
    if (!link.url.trim()) return
    // The dialog edits the field and range it opened on; a peer may have removed them meanwhile.
    const target = api.restore(link.target)
    const intent = target && applyLink(target, { url: link.url, preview: !!link.preview, links: link.links })
    if (!intent) { setLink(null); return }
    if (link.preview) api.dispatchWithHistory(intent, link.preview)
    else api.dispatch(intent)
    setLink(null); api.root?.focus()
  }
  return <OverlayPortal api={api}>
    {link && <form ref={setDialog} role="dialog" tabIndex={-1} aria-label="Edit link" data-testid="content-blocks-editor-link-editor"
      className="border-border bg-surface-0 text-on-surface fixed z-[1100] flex w-[min(20rem,calc(100vw-1rem))] items-center gap-1.5 rounded-lg border p-1.5 shadow-lg" style={position}
      onSubmit={event => { event.preventDefault(); apply() }}>
      <Input ref={input} aria-label="Link URL" data-testid="content-blocks-editor-link-url" value={link.url}
        className="h-8 min-w-0 flex-1 rounded-md px-2 text-sm"
        placeholder="Paste or type a URL" onInput={event => { setLink({ ...link, url: event.currentTarget.value }) }}
        onKeyDown={event => {
          // nux/keys/undo-in-link-input: Notion swallows Cmd+Z / Cmd+Shift+Z / Cmd+Y in the link field -- neither the
          // field's native input history nor the page undoes (notion-browser-queue.md 231, run6 C23). Stop the key so
          // the browser input undo and the editor keymap both never see it.
          if ((event.metaKey || event.ctrlKey) && ['z', 'y'].includes(event.key.toLowerCase())) {
            event.preventDefault(); event.stopPropagation()
          }
          if (event.key === 'Escape') {
            event.preventDefault(); event.stopPropagation()
            const intent = closeLink({ url: link.url, preview: !!link.preview })
            if (intent && link.preview && api.restore(link.target)) {
              api.dispatchWithHistory(intent, link.preview)
            }
            setLink(null); api.root?.focus()
          }
        }} />
      <Button size="sm" type="submit" data-testid="content-blocks-editor-link-apply" disabled={!link.url.trim()}>Apply</Button>
      {/* An existing link offers its removal (Lexical link editor trash button; Notion link menu Remove link). */}
      {!link.preview && link.links.length > 0 && <IconButton size="sm" type="button" semanticRole="secondary" variant="text"
        aria-label="Remove link" data-testid="content-blocks-editor-link-remove" onClick={removeLink}><Trash2Icon />Remove link</IconButton>}
    </form>}
    {open && <div ref={setChooser} role="listbox" aria-label="Pages" data-testid="content-blocks-editor-page-mention-menu" aria-busy={searchState === 'loading'}
      className={`${panelClass} max-h-64 w-64 overflow-y-auto`} style={position}>
      <div className="flex flex-col gap-0.5">{results.map((result, itemIndex) => {
        const id = typeof result === 'string' ? result : result.id
        const ref = resolvePageRef({ pageId: id, spaceId: typeof result === 'string' ? undefined : result.spaceId }, api.spaceId ?? '')
        const resolved = api.resolvePage?.(ref)
        const label = typeof result === 'string' ? resolved?.label ?? id : result.label ?? result.title ?? resolved?.label ?? id
        const emoji = typeof result === 'string' ? resolved?.emoji : result.emoji ?? resolved?.emoji
        return <Button semanticRole="secondary" size="sm" variant="text" key={`${ref.spaceId}:${id}`} type="button" role="option" aria-selected={index === itemIndex}
          className="text-on-surface hover:bg-primary/8 aria-selected:bg-primary/8 h-9 w-full justify-start rounded-md px-2 text-left text-sm font-normal [&>span]:w-full [&>span]:justify-start" data-testid={`content-blocks-editor-page-mention-item-${segment(id)}`}
          onMouseDown={event => { event.preventDefault() }} onMouseEnter={() => { setIndex(itemIndex) }} onClick={() => { choose(result) }}>{emoji ? <span aria-hidden>{emoji}</span> : null}<span className="truncate">{label}</span></Button>
      })}</div>
      {results.length === 0 && <p className="text-on-surface-secondary px-2 py-3 text-sm">{searchState === 'loading' ? 'Searching pages…' : searchState === 'error' ? 'Could not load pages' : 'No pages found'}</p>}
    </div>}
  </OverlayPortal>
}
